import { useCallback, useMemo } from "react";
import { flatten } from "lodash";
import { useSet } from "@circle-react/hooks/useSet";
import { useFilterUrl } from "@circle-react-shared/Filter";
import type { FilterObject } from "./types";

interface UseDynamicFiltersParams {
  defaultFilterItems: string[];
  filters: FilterObject[];
}

interface SplitFiltersResult {
  availableFilters: FilterObject[];
  displayedFilters: FilterObject[];
}

interface UseDynamicFiltersReturn {
  getSplitAndAvailableFilters: (filters: FilterObject[]) => SplitFiltersResult;
  isClearFiltersShown: boolean;
  onAddFilter: (name: string) => void;
  onClearFilters: () => void;
  onRemoveFilter: (value: unknown, name: string) => void;
}

export const useDynamicFilters = ({
  filters,
  defaultFilterItems,
}: UseDynamicFiltersParams): UseDynamicFiltersReturn => {
  const { queryParams, removeQueryParams } = useFilterUrl();
  const allFilterKeys = flatten(filters.map(filter => filter.filterKeys));

  const getInitialDisplayedFilters = (): Set<string> => {
    const initialDisplayedFiltersSet = new Set(defaultFilterItems);

    filters.forEach((filter: FilterObject) => {
      const isExistingOnQueryString: boolean = Object.keys(
        queryParams,
      ).includes(filter.name);
      if (isExistingOnQueryString) {
        initialDisplayedFiltersSet.add(filter.name);
      }
      const hasValue = (filter as FilterObject & { value?: unknown }).value;
      if (hasValue != undefined) {
        initialDisplayedFiltersSet.add(filter.name);
      }
    });
    return initialDisplayedFiltersSet;
  };
  const [displayedFilters, { add, remove, clear }] = useSet<string>(
    getInitialDisplayedFilters(),
  );

  const isClearFiltersShown = useMemo(() => {
    let offset = 0;
    for (const filterItemName of defaultFilterItems) {
      if (Object.prototype.hasOwnProperty.call(queryParams, filterItemName)) {
        return true;
      }
      if (displayedFilters.has(filterItemName)) {
        offset += 1;
      }
    }
    return displayedFilters.size > offset;
  }, [displayedFilters, queryParams, defaultFilterItems]);

  const handleClearFilters = (): void => {
    removeQueryParams([...allFilterKeys]);
    clear();
    defaultFilterItems.forEach(add);
  };

  const getSplitAndAvailableFilters = useCallback(
    (filters: FilterObject[]): SplitFiltersResult => {
      const displayedFilterNames: string[] = Array.from(displayedFilters);
      if (!displayedFilterNames.length) {
        return {
          availableFilters: filters,
          displayedFilters: [],
        };
      }
      return {
        availableFilters: filters.filter(
          (filter: FilterObject) => !displayedFilterNames.includes(filter.name),
        ),
        displayedFilters: filters.filter((filter: FilterObject) =>
          displayedFilterNames.includes(filter.name),
        ),
      };
    },
    [displayedFilters],
  );

  const handleAddFilter = (name: string): void => {
    add(name);
  };

  const handleRemoveFilter = (value: unknown, name: string): void => {
    if (value) return;

    remove(name);
  };

  return {
    isClearFiltersShown,
    getSplitAndAvailableFilters,
    onClearFilters: handleClearFilters,
    onAddFilter: handleAddFilter,
    onRemoveFilter: handleRemoveFilter,
  };
};
